import classnames from 'classnames';
import { addDays, isAfter } from 'date-fns';
import React from 'react';
import { connect } from 'react-redux';
import { formattedDateString } from 'utils/dateUtils';
import { openChildInput, openInput } from '../../../actions/baseActions';
import { selectFromDate, selectFromTime, selectToDate } from '../../../actions/carActions';
import Helper from '../../modules/Helper';
import DatePicker from '../Common/DatePicker';
import FadeTransition from '../Common/FadeTransition';
import TimePicker from '../Common/TimePicker';

const TIME_PICKER_NAME = 'from-time-picker';
type DatePickerFromProps = {
	name: string;
	autoJump?: boolean;
	preventSetInputBoxClassName?: boolean;
	lng: string;
	openInput: (name?: string) => void;
	openChildInput: (name?: string) => void;
	selectFromTime: (time: string) => void;
	selectToDate: (date: Date) => void;
	selectFromDate: (date: Date) => void;
};

type DatePickerFromState = {
	datepicker: boolean;
	timepicker: boolean;
	in: boolean;
};

type AllProps = DatePickerFromProps & DatePickerFromReduxProps;

class DatePickerFrom extends React.Component<AllProps, DatePickerFromState> {
	constructor(props: AllProps) {
		super(props);
		this.state = {
			datepicker: false,
			timepicker: false,
			in: false,
		};
	}

	onSelectDate = (from: Date, keyboardNavigation = false) => {
		let to = this.props.selectedToDate;

		if (from === null) {
			from = new Date();
		}

		if (to === null) {
			to = addDays(from, 1);
		}

		if (isAfter(from, to)) {
			to = from;
		}

		this.props.selectFromDate(from);
		this.props.selectToDate(to);

		if (!keyboardNavigation) {
			if (this.props.autoJump) {
				this.props.openChildInput('to-date-picker');
				this.props.openInput('date-to');
			} else {
				this.props.openInput();
				this.props.openChildInput();
			}
		}
		this.props.openInput('date-to');
	};

	openDatePicker = () => {
		this.openDatePickerBase(!this.state.datepicker);
	};

	openDatePickerBase(open: boolean) {
		this.setState(
			{
				timepicker: false,
				datepicker: open,
				in: open,
			},
			() => {
				if (this.state.in) {
					this.props.openChildInput('from-date-picker');
					this.props.openInput(this.props.name);
				} else {
					this.props.openInput();
				}
			}
		);
	}

	openTimePicker = () => {
		this.openTimePickerBase(!this.state.timepicker);
	};

	openTimePickerBase = (open: boolean) => {
		this.setState(
			{
				datepicker: false,
				timepicker: open,
				in: open,
			},
			() => {
				if (this.state.in) {
					this.props.openInput(this.props.name);
					this.props.openChildInput(TIME_PICKER_NAME);
				} else {
					this.props.openInput();
				}
			}
		);
	};
	onSelectTime = (time: string) => {
		this.setState(
			{
				timepicker: false,
				in: false,
			},
			() => {
				this.props.selectFromTime(time);
				this.props.openInput();
				this.props.openChildInput();
			}
		);
	};

	componentDidUpdate(prevProps: AllProps) {
		if (!this.props.openedInput && this.props.openedInput !== prevProps.openedInput) {
			this.setState({
				datepicker: false,
				timepicker: false,
				in: false,
			});
		} else if (this.props.openedInput === prevProps.name && this.props.openedInput !== prevProps.openedInput) {
			if (this.props.openedChildInput === 'from-date-picker') {
				this.openDatePickerBase(true);
			}
		}
	}

	render() {
		const { datepicker, timepicker } = this.state;
		const {
			lng,
			selectedFromDate,
			name,
			openedChildInput,
			openedInput,
			selectedFromTime,
			preventSetInputBoxClassName,
		} = this.props;
		const isInputOpened = openedInput === name;

		const openedDatePicker = openedInput && datepicker && openedChildInput === 'from-date-picker';
		const openedTimePicker = openedInput && timepicker && openedChildInput === 'from-time-picker';
		const formattedDate = formattedDateString({ lng, date: selectedFromDate });

		return (
			<div
				id={name}
				className={classnames('flex-1', {
					'open-input': isInputOpened,
					'input-box': !preventSetInputBoxClassName,
				})}
			>
				<div
					className={classnames('date-picker-parent input-control justify-content-between filled', {
						focused: isInputOpened,
					})}
					aria-label={formattedDate.ariaLabelString}
				>
					<label htmlFor={`${name}-input`} className="hidden-input-label">
						<input type="text" className="hidden-input" id={`${name}-input`} />
					</label>
					<div
						className={classnames('date-picker d-flex align-items-center flex-2 h-100', {
							'focused-child': openedDatePicker,
						})}
						onClick={this.openDatePicker}
					>
						<i className="icon-date" />
						<div className="selected">
							{formattedDate.renderText}
						</div>
					</div>
					<div
						className={classnames('d-flex align-items-center flex-1 h-100', {
							'focused-child': openedTimePicker,
						})}
						onClick={this.openTimePicker}
					>
						<div className="timepicker-show">
							<div className="selected-time">
								{selectedFromTime === '12:00' ? Helper.trans('keywords.noon') : selectedFromTime}
							</div>
							<div
								className={classnames('icon')}
								style={{
									transition: 'all .2s ease',
									transform: isInputOpened && timepicker ? 'rotate(180deg)' : 'none',
								}}
							>
								<i className="icon-arrow opened" />
							</div>
						</div>
					</div>
				</div>
				<div style={{ position: 'relative' }}>
					<FadeTransition timeout={500} in={openedDatePicker}>
						<DatePicker before={new Date()} selected={selectedFromDate as Date} onSelectDate={this.onSelectDate} />
					</FadeTransition>
					<FadeTransition timeout={500} in={openedTimePicker}>
						<TimePicker selected={selectedFromTime} onSelect={this.onSelectTime} />
					</FadeTransition>
				</div>
			</div>
		);
	}
}

const mapStateToProps = (state: any) => {
	const { selectedFromDate, selectedToDate, selectedFromTime } = state.car;
	const { openedInput, openedChildInput, lng } = state.base;
	return {
		selectedFromDate,
		selectedToDate,
		selectedFromTime,
		openedInput,
		openedChildInput,
	};
};

type DatePickerFromReduxProps = ReturnType<typeof mapStateToProps>;

const mapDispatchToProps = {
	selectFromDate,
	selectToDate,
	selectFromTime,
	openInput,
	openChildInput,
};

export default connect(mapStateToProps, mapDispatchToProps)(DatePickerFrom);
